import { useContext, useMemo } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'

import { FeedbackContext } from '~/components/feedback'
import Loading from '~/components/Loading'
import { getIsNetwork, getWhiteLabelPublisherId, history } from '~/helpers'
import { useAlert } from '~/hooks/useAlert'
import { useAppInfo } from '~/hooks/useAppInfo'
import useQueryParams from '~/hooks/useQuery'
import { adTypesOptions } from '~/modules/retailMedia/constants/adTypesOptions'
import { useAdTypes } from '~/modules/retailMedia/hooks/useAdTypes'
import { usePublisherConfig } from '~/modules/retailMedia/hooks/usePublisherConfig'
import { patchLinkPublisherAdvertiserService } from '~/modules/retailMedia/services/advertiser-publisher'

import FormEditLink from '../../../components/FormEditLink'
import { useAdvertiserPublisherLinks } from '../../hooks/useAdvertiserPublisherLinks'

// import { Container } from './styles';

interface FormData {
  type: Type<CampaignAdType>
  seller_id?: string
  brands?: string[]
  defaultStrategy?: CampaignStrategy
  strategies?: Type<CampaignStrategy>
  allow_total_budget_campaign?: boolean
  enable_audience_campaign?: boolean
  enable_creative_studio?: boolean
  allow_manage_subpublishers?: boolean
  enable_impression_share: boolean
}

interface Type<T = string> {
  all: All<T>[]
  allObject: AllObject
  filtered: T[]
}

interface AllObject {
  type_product: boolean
  type_banner: boolean
  type_sponsored_brand: boolean
}

interface All<T = string> {
  name: string
  value: T
  checked: boolean
}

const EditAdvertiserLink: React.FC = () => {
  const { linkConfig } = useAppInfo()
  const { toast } = useContext(FeedbackContext)

  const {
    data: adTypes = [],
    error,
    isPending
  } = useAdTypes({ useRMID: false })

  const [AlertError] = useAlert(error)

  const params = useQueryParams()
  const advertiserId = params.get('rmid')

  const {
    data: advertiserLinkData,
    queryKey: queryKeyAdvertiserLink,
    isPending: isPendingAdvertiserLink,
    queryKey
  } = useAdvertiserPublisherLinks({
    useAdvertiserId: !!advertiserId
  })

  const advertiserLink = useMemo(() => {
    const advertiser = advertiserLinkData?.data?.advertisers?.[0]

    return advertiser?.advertiser_id === advertiserId ? advertiser : null
  }, [advertiserId, advertiserLinkData?.data?.advertisers])

  const { data: publisherConfig } = usePublisherConfig({
    publisherId: getWhiteLabelPublisherId() || advertiserLink?.publisher_id
  })
  const allowedFields = useMemo(() => {
    const publisherFeatures: LinkConfig[] = []

    if (publisherConfig?.allow_manage_subpublishers && getIsNetwork()) {
      publisherFeatures.push('manageSubpublishers')
    }
    if (publisherConfig?.allow_audience_campaign) {
      publisherFeatures.push('audience')
    }
    if (publisherConfig?.allow_creative_studio) {
      publisherFeatures.push('creativeStudio')
    }
    if (publisherConfig?.allow_simplified_campaign) {
      publisherFeatures.push('strategies')
    }
    if (publisherConfig?.allow_total_budget_campaign) {
      publisherFeatures.push('totalBudget')
    }

    return [...linkConfig, ...publisherFeatures, 'impressionShare']
  }, [linkConfig, publisherConfig])

  const strategiesTypes = useMemo(
    () => advertiserLink?.strategy_types,
    [advertiserLink?.strategy_types]
  )

  const errorLink = {
    response: {
      data: { message: t('rm:config.advertisers.panel.error.loading') }
    }
  }

  const [AlertErrorLink] = useAlert(
    !isPendingAdvertiserLink && advertiserLink === null ? errorLink : false
  )

  const initialImpressionShareEnabled =
    advertiserLink?.enable_impression_share ?? true
  const initialManageSubpublishers =
    advertiserLink?.allow_manage_subpublishers ?? true

  const initialData = useMemo(
    () => ({
      ...advertiserLink,
      enable_impression_share: initialImpressionShareEnabled,
      allow_manage_subpublishers: initialManageSubpublishers,
      defaultStrategy: advertiserLink?.default_strategy_type,
      strategies: ['objective_based', 'standard']?.reduce(
        (acc, item) => ({
          ...acc,
          [`strategy_${item}`]: strategiesTypes?.includes(item)
        }),
        {}
      ),
      type: adTypes?.reduce(
        (acc, item) => ({
          ...acc,
          [`type_${item}`]: advertiserLink?.ad_types?.includes(item)
        }),
        {}
      )
    }),
    [
      adTypes,
      advertiserLink,
      initialImpressionShareEnabled,
      initialManageSubpublishers,
      strategiesTypes
    ]
  )

  const handleClosePanel = () => {
    const newParams = new URLSearchParams(params.toString())
    newParams.delete('rmid')
    history.push({ search: newParams.toString() })
  }

  /**
   * Handle update
   */

  const queryClient = useQueryClient()

  const patchAdvertiserAdTypesMutation = useMutation({
    mutationFn: patchLinkPublisherAdvertiserService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey
      })

      queryClient.invalidateQueries({
        queryKey: queryKeyAdvertiserLink
      })

      toast.success(t('common:messages.success.message'))

      handleClosePanel()
    },
    onError: () => {
      toast.critical(t('common:messages.errorOccurred.message'))
    }
  })

  const handleUpdateSubmit = async (data: FormData) => {
    const impressionShareChanged =
      data.enable_impression_share !== initialImpressionShareEnabled
    const manageSubpublishers = data.allow_manage_subpublishers
    const body: AdvertiserPublisherLinkPatchRequestBody = {
      seller_id: data.seller_id,
      ad_types: data.type.all
        .filter(item => !!item.checked)
        .map(item => item.value),
      default_strategy_type: data.defaultStrategy,
      strategy_types: data?.strategies?.all
        .filter(item => !!item.checked)
        .map(item => item.value),
      brands: data?.brands || [],
      allow_total_budget_campaign: data?.allow_total_budget_campaign,
      enable_audience_campaign: data?.enable_audience_campaign,
      enable_creative_studio: data?.enable_creative_studio
    }

    if (impressionShareChanged) {
      body.enable_impression_share = data.enable_impression_share
    }

    if (
      typeof manageSubpublishers === 'boolean' &&
      manageSubpublishers !== initialManageSubpublishers
    ) {
      body.allow_manage_subpublishers = manageSubpublishers
    }

    await patchAdvertiserAdTypesMutation.mutateAsync({
      advertiserId,
      body
    })
  }

  const options = useMemo(
    () =>
      adTypesOptions.filter(item =>
        adTypes.includes(item.value)
      ) as CheckboxOptionProps[],
    [adTypes]
  )

  return (
    <section className="px-3">
      <p>
        {t('rm:config.advertisers.panel.description')}{' '}
        <strong>
          {advertiserLink?.advertiser_name ||
            t('rm:config.advertisers.panel.advertiser.name.default')}
        </strong>
      </p>

      <Loading status={isPending || isPendingAdvertiserLink}>
        {t('common:actions.loading')}...
      </Loading>

      <AlertError />

      <AlertErrorLink />

      {!isPending && advertiserLink && (
        <FormEditLink
          handleCancel={handleClosePanel}
          options={options}
          initialData={initialData}
          onSubmit={handleUpdateSubmit}
          fields={allowedFields}
          isLoading={patchAdvertiserAdTypesMutation.isPending}
          isImpressionShareDisabled={
            publisherConfig?.allow_impression_share === false
          }
        />
      )}
    </section>
  )
}

export default EditAdvertiserLink
